import React, { useState, useEffect } from 'react';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Code, FileText, FlaskConical, Copy, Check, Eye, Upload, Link as LinkIcon, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE_URL } from '@/api/apiConfig';

// 이미지 업로드 전용 URL (CloudFront 우회 - ALB 직접 연결)
// 운영: REACT_APP_UPLOAD_API_URL 로 ALB 직접 주소 지정
// 미설정: API_BASE_URL 그대로 사용
const getUploadBaseUrl = (): string => {
  if (process.env.REACT_APP_UPLOAD_API_URL) {
    return process.env.REACT_APP_UPLOAD_API_URL;
  }
  return API_BASE_URL;
};
const OPEN_API_BASE_URL = API_BASE_URL;
const OPEN_ALT_GENERATE_URL = `${OPEN_API_BASE_URL}/api/open/alt/generate`;
const PUBLIC_OPEN_API_BASE_URL = 'https://alt.aigeneration-tech.com';
const PUBLIC_OPEN_ALT_GENERATE_URL = `${PUBLIC_OPEN_API_BASE_URL}/api/open/alt/generate`;

// 샘플 테스트 사용량 조회는 현재 환경의 API 라우팅을 따른다
const SAMPLE_TEST_BASE_URL = '';

// 이미지 업로드 최대 크기 (환경변수로 관리)
// 운영: .env.production REACT_APP_MAX_UPLOAD_SIZE_MB=10  (S3 Presigned URL 직접 업로드)
// 로컬: .env.development REACT_APP_MAX_UPLOAD_SIZE_MB=20 (Spring Boot 설정값)
const MAX_UPLOAD_SIZE_MB = parseInt(process.env.REACT_APP_MAX_UPLOAD_SIZE_MB || '20', 10);
const MAX_UPLOAD_SIZE_BYTES = MAX_UPLOAD_SIZE_MB * 1024 * 1024;
const MAX_UPLOAD_SIZE_LABEL = `최대 ${MAX_UPLOAD_SIZE_MB}MB`;

// 운영환경에서 S3 직접 업로드 방식 사용 여부
const USE_S3_UPLOAD = process.env.REACT_APP_USE_S3_UPLOAD === 'true';

export default function ApiDocsPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('docs');
  const [codeLang, setCodeLang] = useState<'nodejs' | 'java' | 'python'>('nodejs');
  const [copiedCode, setCopiedCode] = useState(false);
  const [showSourceCode, setShowSourceCode] = useState(false);
  
  // 샘플 테스트 상태
  const [testMethod, setTestMethod] = useState<'url' | 'file'>('url');
  const [imageUrl, setImageUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<string>(''); // PROCESSING, COMPLETED, FAILED
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [useContextProfile, setUseContextProfile] = useState(false);
  const [profileActive, setProfileActive] = useState(false);
  const [rateLimitRetryAfter, setRateLimitRetryAfter] = useState<number | null>(null);
  const [manualApiKey, setManualApiKey] = useState('');
  
  // 샘플 테스트 사용 여부 추적 (비활성화 - 백엔드 엔드포인트 없음)
  // const [checkingUsage, setCheckingUsage] = useState(false);
  // const [urlTestUsed, setUrlTestUsed] = useState(false);
  // const [fileTestUsed, setFileTestUsed] = useState(false);
  // const [totalUsedCount, setTotalUsedCount] = useState(0);
  // const [maxCountPerUser, setMaxCountPerUser] = useState<number | null>(null);
  // const [remainingCount, setRemainingCount] = useState<number | null>(null);
  // const [remainingTests, setRemainingTests] = useState<{ url: number; file: number } | null>(null);

  // 로그인 세션별 사용 여부 체크 (비활성화)
  // useEffect(() => {
  //   checkSampleTestUsage();
  // }, [user]);


  useEffect(() => {
    if (rateLimitRetryAfter === null || rateLimitRetryAfter <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setRateLimitRetryAfter((current) => {
        if (current === null || current <= 1) {
          clearInterval(timer);
          return null;
        }
        return current - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [rateLimitRetryAfter]);

  // 컨텍스트 프로필 등록 여부 확인
  useEffect(() => {
    if (!user || !token) return;
    fetch(`${API_BASE_URL}/api/user/alt-profiles/status`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) {
          setProfileActive(data.hasProfile);
          setUseContextProfile(data.hasProfile && data.useYn === 'Y');
        }
      })
      .catch(() => { /* 무시 */ });
  }, [user]);

  // 샘플 테스트 사용 여부 확인 (비활성화 - 백엔드 엔드포인트 없음)
  // const checkSampleTestUsage = async () => {
  //   if (!user || !token) {
  //     setCheckingUsage(false);
  //     return;
  //   }

  //   try {
  //     setCheckingUsage(true);
      
  //     // 백엔드에서 현재 로그인 세션의 샘플 테스트 사용 여부 확인
  //     const response = await fetch(`${SAMPLE_TEST_BASE_URL}/api/open/sample-test/usage`, {
  //       method: 'GET',
  //       headers: {
  //         'Authorization': `Bearer ${token}`
  //       }
  //     });

  //     if (response.ok) {
  //       const data = await response.json();
  //       setUrlTestUsed(data.urlTestUsed || false);
  //       setFileTestUsed(data.fileTestUsed || false);
  //       if (data.totalUsedCount !== undefined) setTotalUsedCount(data.totalUsedCount);
  //       if (data.maxCountPerUser !== undefined) setMaxCountPerUser(data.maxCountPerUser);
  //       if (data.remainingCount !== undefined) setRemainingCount(data.remainingCount);
  //     }
  //   } catch (error) {
  //     console.error('샘플 테스트 사용 여부 확인 실패:', error);
  //     // 에러 시 기본값 유지 (false)
  //   } finally {
  //     setCheckingUsage(false);
  //   }
  // };

  // 코드 복사
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  // 샘플 코드 (2026-03-28 BE 피드백 반영)
  const sampleCodesUrl = {
    nodejs: `// 이미지 URL
const response = await fetch('${PUBLIC_OPEN_ALT_GENERATE_URL}', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-KEY': '발급받은-UUID-형태-API-KEY' // alt_ 접두사 없음
  },
  body: JSON.stringify({
    imageUrl: 'https://example.com/image.jpg',
    useContextProfile: false
    // useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
    // 컨텍스트 프로필을 사용하려면 true로 변경하세요
  })
});
const result = await response.json();
// result.status: "COMPLETED" | "FAILED"
// result.altText: 생성된 ALT 텍스트
// result.generationType: "AI" | "FALLBACK" | "ERROR"
console.log('ALT 텍스트:', result.altText);
if (result.generationType === 'ERROR') {
  // 이미지 자체에 문제 → 사용자에게 이미지 확인 요청
}
// 이미 완료된 결과를 나중에 다시 조회하고 싶을 때만 사용
// GET /api/open/alt/result/{result.requestId}
`,
    java: `// 이미지 URL
HttpClient client = HttpClient.newHttpClient();
String body = "{\"imageUrl\":\"https://example.com/image.jpg\",\"useContextProfile\":false}";
// useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
// 컨텍스트 프로필을 사용하려면 true로 변경하세요
HttpRequest request = HttpRequest.newBuilder()
    .uri(URI.create("${PUBLIC_OPEN_ALT_GENERATE_URL}"))
    .header("Content-Type", "application/json")
    .header("X-API-KEY", "발급받은-UUID-형태-API-KEY")
    .POST(HttpRequest.BodyPublishers.ofString(body))
    .build();
HttpResponse<String> res = client.send(request, HttpResponse.BodyHandlers.ofString());
JSONObject result = new JSONObject(res.body());
// result.getString("status"): "COMPLETED" | "FAILED"
// result.getString("altText"): 생성된 ALT 텍스트
// result.getString("generationType"): "AI" | "FALLBACK" | "ERROR"
System.out.println("ALT 텍스트: " + result.getString("altText"));
if ("ERROR".equals(result.getString("generationType"))) {
  // 이미지 자체에 문제 → 사용자에게 이미지 확인 요청
}
`,
    python: `import requests
# 이미지 URL
response = requests.post(
    '${PUBLIC_OPEN_ALT_GENERATE_URL}',
    headers={
        'Content-Type': 'application/json',
        'X-API-KEY': '발급받은-UUID-형태-API-KEY'  # alt_ 접두사 없음
    },
    json={
        'imageUrl': 'https://example.com/image.jpg',
        'useContextProfile': False
        # useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
        # 컨텍스트 프로필을 사용하려면 true로 변경하세요
    }
)
result = response.json()
# result['status']: "COMPLETED" | "FAILED"
# result['altText']: 생성된 ALT 텍스트
# result['generationType']: "AI" | "FALLBACK" | "ERROR"
print(f"ALT 텍스트: {result['altText']}")
if result.get('generationType') == 'ERROR':
    # 이미지 자체에 문제 → 사용자에게 이미지 확인 요청
    pass
`
  };

  const sampleCodesUpload = {
    nodejs: `// 이미지 업로드 (S3 Presigned URL 방식)
// useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
// 컨텍스트 프로필을 사용하려면 true로 변경하세요
const fs = require('fs');
const apiKey = '발급받은-UUID-형태-API-KEY';

async function uploadFileAndGenerateAlt(filePath) {
  // 1. Presigned URL 요청
  const presignedRes = await fetch('${PUBLIC_OPEN_API_BASE_URL}/api/open/alt/presigned-url', {
    method: 'POST',
    headers: {
      'X-API-KEY': apiKey,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      filename: fs.basename(filePath),
      contentType: 'image/jpeg'
    })
  });
  const { uploadUrl, finalUrl } = await presignedRes.json();

  // 2. S3에 직접 업로드
  const fileBuffer = fs.readFileSync(filePath);
  await fetch(uploadUrl, {
    method: 'PUT',
    body: fileBuffer
  });

  // 3. ALT 생성 (s3Url 파라미터)
  const response = await fetch('${PUBLIC_OPEN_ALT_GENERATE_URL}', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-API-KEY': apiKey
    },
    body: JSON.stringify({
      s3Url: finalUrl,
      useContextProfile: false
    })
  });

  const result = await response.json();
  console.log('ALT 텍스트:', result.altText);
  return result;
}
`,
    java: `// 이미지 업로드 (S3 Presigned URL 방식)
// useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
// 컨텍스트 프로필을 사용하려면 true로 변경하세요
import java.nio.file.*;
import java.net.http.*;
String API_KEY = "발급받은-UUID-형태-API-KEY";
Path filePath = Paths.get("image.jpg");
String filename = filePath.getFileName().toString();
String contentType = "image/jpeg";

// 1. Presigned URL 요청
String presignedBody = String.format("{\"filename\":\"%s\",\"contentType\":\"%s\"}", filename, contentType);
HttpRequest presignedReq = HttpRequest.newBuilder()
    .uri(URI.create("${PUBLIC_OPEN_API_BASE_URL}/api/open/alt/presigned-url"))
    .header("Content-Type", "application/json")
    .header("X-API-KEY", API_KEY)
    .POST(HttpRequest.BodyPublishers.ofString(presignedBody))
    .build();
HttpResponse<String> presignedRes = client.send(presignedReq, HttpResponse.BodyHandlers.ofString());
JSONObject presignedData = new JSONObject(presignedRes.body());
String uploadUrl = presignedData.getString("uploadUrl");
String finalUrl = presignedData.getString("finalUrl");

// 2. S3에 직접 업로드
byte[] fileBytes = Files.readAllBytes(filePath);
HttpRequest uploadReq = HttpRequest.newBuilder()
    .uri(URI.create(uploadUrl))
    .PUT(HttpRequest.BodyPublishers.ofByteArray(fileBytes))
    .build();
client.send(uploadReq, HttpResponse.BodyHandlers.ofString());

// 3. ALT 생성 (s3Url 파라미터)
String altBody = String.format("{\"s3Url\":\"%s\",\"useContextProfile\":false}", finalUrl);
HttpRequest altReq = HttpRequest.newBuilder()
    .uri(URI.create("${PUBLIC_OPEN_ALT_GENERATE_URL}"))
    .header("Content-Type", "application/json")
    .header("X-API-KEY", API_KEY)
    .POST(HttpRequest.BodyPublishers.ofString(altBody))
    .build();
HttpResponse<String> altRes = client.send(altReq, HttpResponse.BodyHandlers.ofString());
JSONObject result = new JSONObject(altRes.body());
System.out.println("ALT 텍스트: " + result.getString("altText"));
`,
    python: `import requests
# 이미지 업로드 (S3 Presigned URL 방식)
# useContextProfile: 개인별 컨텍스트 프로필 적용 여부 (기본값: true)
# 컨텍스트 프로필을 사용하려면 true로 변경하세요
API_KEY = '발급받은-UUID-형태-API-KEY'

def upload_file_and_generate_alt(file_path):
    filename = file_path.split('/')[-1]
    content_type = 'image/jpeg'
    
    # 1. Presigned URL 요청
    presigned_res = requests.post(
        '${PUBLIC_OPEN_API_BASE_URL}/api/open/alt/presigned-url',
        headers={
            'X-API-KEY': API_KEY,
            'Content-Type': 'application/json'
        },
        json={
            'filename': filename,
            'contentType': content_type
        }
    )
    presigned_data = presigned_res.json()
    upload_url = presigned_data['uploadUrl']
    final_url = presigned_data['finalUrl']
    
    # 2. S3에 직접 업로드
    with open(file_path, 'rb') as f:
        requests.put(upload_url, data=f)
    
    # 3. ALT 생성 (s3Url 파라미터)
    response = requests.post(
        '${PUBLIC_OPEN_ALT_GENERATE_URL}',
        headers={
            'Content-Type': 'application/json',
            'X-API-KEY': API_KEY
        },
        json={
            's3Url': final_url,
            'useContextProfile': False
        }
    )
    
    result = response.json()
    print(f'ALT 텍스트: {result["altText"]}')
    return result
`,
  };

  // 테스트 실행
  const handleRunTest = async () => {
    if (!user || !token) {
      setTestError('테스트를 사용하려면 로그인이 필요합니다.');
      return;
    }

    if (!manualApiKey.trim()) {
      setTestError('API Key를 입력해주세요.');
      return;
    }

    // 입력 검증
    if (testMethod === 'url' && !imageUrl.trim()) {
      setTestError('이미지 URL을 입력해주세요.');
      return;
    }
    if (testMethod === 'file' && !selectedFile) {
      setTestError('파일을 선택해주세요.');
      return;
    }

    setTestLoading(true);
    setTestError(null);
    setTestResult(null);
    setRateLimitRetryAfter(null);
    if (filePreviewUrl) { URL.revokeObjectURL(filePreviewUrl); setFilePreviewUrl(null); }

    try {
      let response;

      if (testMethod === 'file' && selectedFile) {
        // 이미지 업로드 방식 - 환경별 분기 처리
        if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
        setFilePreviewUrl(URL.createObjectURL(selectedFile));

        if (USE_S3_UPLOAD) {
          // 운영: S3 직접 업로드 방식
          // 1. presigned-url 호출
          const presignedRes = await fetch(`${PUBLIC_OPEN_API_BASE_URL}/api/open/alt/presigned-url`, {
            method: 'POST',
            headers: {
              'X-API-KEY': manualApiKey.trim(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              filename: selectedFile.name,
              contentType: selectedFile.type
            })
          });
          if (!presignedRes.ok) {
            const err = await presignedRes.json().catch(() => ({ message: 'Presigned URL 요청 실패' }));
            throw new Error(err.message || 'Presigned URL 요청 실패');
          }
          const { uploadUrl, s3Key, finalUrl } = await presignedRes.json();

          // 2. S3에 직접 업로드
          const s3UploadRes = await fetch(uploadUrl, {
            method: 'PUT',
            body: selectedFile
          });
          if (!s3UploadRes.ok) {
            throw new Error('S3 업로드 실패');
          }

          // 3. ALT 생성 (s3Url 파라미터)
          response = await fetch(PUBLIC_OPEN_ALT_GENERATE_URL, {
            method: 'POST',
            headers: {
              'X-API-KEY': manualApiKey.trim(),
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ s3Url: finalUrl, useContextProfile })
          });
        } else {
          // 개발: multipart/form-data 유지
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('useContextProfile', String(useContextProfile));
          response = await fetch(OPEN_ALT_GENERATE_URL, {
            method: 'POST',
            headers: { 'X-API-KEY': manualApiKey.trim() },
            body: formData
          });
        }
      } else {
        // URL 방식 - JSON body
        response = await fetch(OPEN_ALT_GENERATE_URL, {
          method: 'POST',
          headers: { 'X-API-KEY': manualApiKey.trim(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl: imageUrl.trim(), useContextProfile })
        });
      }
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('text/html')) throw new Error('서버가 HTML을 반환했습니다. CORS 또는 CloudFront 설정을 확인하세요.');
      if (!response.ok) {
        const err = await response.json().catch(() => ({ message: 'API 호출 실패' }));
        // HTTP 오류 코드별 고객 친화적 메시지
        if (response.status === 400) {
          throw new Error('요청 형식이 올바르지 않습니다. 입력값을 확인해주세요.');
        }
        if (response.status === 401) {
          throw new Error('API KEY가 올바르지 않습니다. 발급받은 API KEY를 정확히 입력했는지 확인해주세요.');
        }
        if (response.status === 403) {
          throw new Error('접근 권한이 없습니다. API KEY가 활성 상태인지 확인해주세요.');
        }
        if (response.status === 404) {
          throw new Error('API 엔드포인트를 찾을 수 없습니다. 서비스 상태를 확인해주세요.');
        }
        if (response.status === 429) {
          throw new Error('요청 횟수가 제한되었습니다. 잠시 후 다시 시도해주세요.');
        }
        if (response.status >= 500) {
          throw new Error('서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
        }
        throw new Error(err.message || `HTTP ${response.status}`);
      }
      const result = await response.json();
      setTestResult({
        ...result,
        status: result.status || 'COMPLETED',
        testMethod: testMethod === 'url' ? 'URL' : 'FILE',
        imageUrl: testMethod === 'url' ? imageUrl.trim() : undefined,
      });
      setTestStatus('COMPLETED');
    } catch (error: any) {
      setTestError(error.message || '테스트 실행 중 오류가 발생했습니다.');
    } finally {
      setTestLoading(false);
    }
    return;
  };

  // 결과 폴링 함수
  const pollTestResult = async (requestId: string, authToken: string, maxAttempts = 15) => {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const resultResponse = await fetch(`${SAMPLE_TEST_BASE_URL}/api/open/sample-test/result/${requestId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });

        if (!resultResponse.ok) {
          const errorData = await resultResponse.json().catch(() => ({ message: '결과 조회 실패' }));
          throw new Error(errorData.message || `HTTP ${resultResponse.status}`);
        }

        const result = await resultResponse.json();
        setTestStatus(result.status);

        if (result.status === 'COMPLETED') {
          setTestResult(result);
          // 이미지 업로드인 경우 로컬 파일로 미리보기 URL 생성
          if (testMethod === 'file' && selectedFile) {
            setFilePreviewUrl(URL.createObjectURL(selectedFile));
          }
          setTestLoading(false);
          
          // 사용 완료 표시 (비활성화 - 백엔드 엔드포인트 없음)
          // if (testMethod === 'url') {
          //   setUrlTestUsed(true);
          // } else {
          //   setFileTestUsed(true);
          // }
          // setTotalUsedCount(prev => prev + 1);
          // setRemainingCount(prev => prev !== null ? prev - 1 : null);
          return;
        } else if (result.status === 'FAILED') {
          setTestError(result.errorMessage || 'ALT 텍스트 생성에 실패했습니다.');
          setTestLoading(false);
          return;
        }

        // PROCESSING 상태 - 2초 후 재시도
        await new Promise(resolve => setTimeout(resolve, 2000));

      } catch (error: any) {
        setTestError(error.message || '결과 조회 중 오류가 발생했습니다.');
        setTestLoading(false);
        return;
      }
    }

    // 타임아웃
    setTestError('결과 조회 시간이 초과되었습니다. 나중에 다시 시도해주세요.');
    setTestLoading(false);
  };

  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || 'API 연동 가이드'}
        description="외부 시스템에서 ALT 텍스트 생성 API를 사용하는 방법을 안내합니다."
        icon={menuIcon}
      />

      {/* API Key 안내 */}
      <Alert>
        <AlertDescription>
          <div className="flex items-start gap-2">
            <span className="font-semibold">💡 API Key 발급:</span>
            <div>
              <a href="/dashboard/ApiKeyManagement" className="text-blue-600 underline hover:text-blue-800">
                API Key 관리 페이지
              </a>
              에서 API Key를 발급받으세요.
            </div>
          </div>
        </AlertDescription>
      </Alert>

      {/* 탭 구조 */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="docs">
            <FileText className="w-4 h-4 mr-2" />
            API 문서
          </TabsTrigger>
          <TabsTrigger value="samples">
            <Code className="w-4 h-4 mr-2" />
            샘플 코드
          </TabsTrigger>
          {/* <TabsTrigger value="test" disabled={!user}>
            <FlaskConical className="w-4 h-4 mr-2" />
            샘플 테스트 {!user && '(로그인 필요)'}
          </TabsTrigger> */}
          <TabsTrigger value="test" disabled={!user}>
            <FlaskConical className="w-4 h-4 mr-2" />
            <span className="flex flex-col items-start leading-tight">
              <span>샘플 테스트</span>
              <span className="text-[10px] opacity-70">Node.js{!user && ' · 로그인 필요'}</span>
            </span>
          </TabsTrigger>
        </TabsList>

        {/* API 문서 탭 */}
        <TabsContent value="docs" className="space-y-6 mt-6">
          {/* 샘플 테스트 vs 정식 API 비교 */}
          <Card className="bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg">💡 API 사용 방법</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {/* 샘플 테스트 안내 제거, 정식 API Key 안내만 노출 */}
              <div>
                <strong className="text-blue-800">1. 정식 API Key 사용</strong>
                <p className="text-gray-700 mt-1">
                  <a href="/dashboard/ApiKeyManagement" className="text-blue-600 underline">API Key 관리</a>에서 정식 API Key를 발급받아 
                  <code className="bg-white px-1 py-0.5 rounded mx-1">X-API-Key</code> 헤더로 무제한 사용 가능합니다.
                </p>
              </div>
              <div className="border-l-4 border-amber-400 bg-amber-50 pl-4 py-2 rounded">
                <strong className="text-amber-900">보안 안내</strong>
                <p className="text-gray-700 mt-1">
                  API Key는 브라우저 프론트엔드 코드에 노출하지 마세요. 본 API는 고객사 서버 환경(Node.js, Java, Python 등)에서 호출하는 것을 권장합니다.
                </p>
                <p className="text-gray-700 mt-1">
                  브라우저에서 직접 호출하는 경우 CORS 정책 및 API Key 노출 문제가 발생할 수 있습니다.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>정식 API 엔드포인트</CardTitle>
              <CardDescription>X-API-Key 헤더를 사용하는 프로덕션 API</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                        {/* API Key 형식 안내 */}
                        <div className="border-l-4 border-orange-400 pl-4 py-2 bg-orange-50 rounded">
                          <div className="font-semibold mb-1">2. API Key 형식 (접두사 없음)</div>
                          <table className="text-sm border-collapse">
                            <thead>
                              <tr>
                                <th className="px-2 py-1 border-b font-normal text-gray-500">구분</th>
                                <th className="px-2 py-1 border-b font-normal text-gray-500">내용</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="px-2 py-1 border-b bg-red-50">❌ F/E 예시</td>
                                <td className="px-2 py-1 border-b text-gray-700">alt_발급받은-API-KEY</td>
                              </tr>
                              <tr>
                                <td className="px-2 py-1 border-b bg-green-50">✅ 실제 BE 발급 형식</td>
                                <td className="px-2 py-1 border-b text-gray-700">UUID 형태 xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (하이픈 포함, 접두사 없음)</td>
                              </tr>
                            </tbody>
                          </table>
                          <div className="text-xs text-gray-600 mt-2">BE에서 <span className="font-semibold text-red-600">alt_ 접두사</span>를 붙이지 않습니다. <span className="font-semibold">발급된 키를 그대로 사용하세요.</span></div>
                        </div>
              <div className="border-l-4 border-blue-500 pl-4 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">POST</span>
                  <code className="text-sm font-mono">/api/open/alt/generate</code>
                </div>
                <p className="text-sm text-gray-600">이미지 URL 또는 이미지 업로드를 통한 ALT 텍스트 생성 요청</p>
                <p className="text-xs text-gray-500 mt-1">인증: X-API-Key 헤더 필요</p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4 py-2">
                <div className="flex items-center gap-2 mb-1">
                  <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-semibold">GET</span>
                  <code className="text-sm font-mono">/api/open/alt/result/{'{requestId}'}</code>
                </div>
                <p className="text-sm text-gray-600">생성된 ALT 텍스트 조회</p>
                <p className="text-xs text-gray-500 mt-1">인증: X-API-Key 헤더 필요</p>
              </div>
            </CardContent>
          </Card>


          <Card>
            <CardHeader>
              <CardTitle>인증 방식</CardTitle>
              <CardDescription>모든 API 요청 시 인증 헤더 필요</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-semibold">HTTP Header</p>
                <code className="block bg-gray-800 text-gray-100 p-3 rounded text-sm">
                  X-API-Key: your-uuid-api-key-here
                </code>
                <p className="text-xs text-gray-600 mt-2">
                  * API Key는 <a href="/dashboard/api-key" className="text-blue-600 underline">API Key 관리</a>에서 발급받으며, 발급된 UUID 값을 그대로 사용합니다.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>요청 파라미터</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-sm mb-2">POST /api/open/alt/generate</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border px-3 py-2 text-left">파라미터</th>
                          <th className="border px-3 py-2 text-left">타입</th>
                          <th className="border px-3 py-2 text-left">필수</th>
                          <th className="border px-3 py-2 text-left">설명</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border px-3 py-2"><code>imageUrl</code></td>
                          <td className="border px-3 py-2">string</td>
                          <td className="border px-3 py-2">선택*</td>
                          <td className="border px-3 py-2">이미지 URL (http/https)</td>
                        </tr>
                        <tr>
                          <td className="border px-3 py-2"><code>file</code></td>
                          <td className="border px-3 py-2">file</td>
                          <td className="border px-3 py-2">선택*</td>
                          <td className="border px-3 py-2">이미지 파일 (S3 업로드, 최대 20MB)</td>
                        </tr>
                        <tr>
                          <td className="border px-3 py-2"><code>useContextProfile</code></td>
                          <td className="border px-3 py-2">boolean</td>
                          <td className="border px-3 py-2">선택</td>
                          <td className="border px-3 py-2">개인별 컨텍스트 프로필 적용 여부 (기본값: true)</td>
                        </tr>
                      </tbody>
                    </table>
                    <p className="text-xs text-gray-600 mt-2">* imageUrl 또는 file 중 하나는 필수입니다.</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-sm mb-2">응답 예시</h4>
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-xs overflow-x-auto">
{`{
  "requestId": "a1b2c3d4e5f6...",
  "altText": "빨간 드레스를 입은 여성이 카페에서 커피를 마시고 있습니다.",
  "generationType": "AI",
  "status": "COMPLETED",
  "useContextProfile": true
}`}
                  </pre>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 샘플 코드 탭 */}
        <TabsContent value="samples" className="space-y-6 mt-6">
          {/* 이미지 URL 방식 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>이미지 URL 방식</CardTitle>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCode(sampleCodesUrl[codeLang])}
                >
                  {copiedCode ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      복사됨
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      복사
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={codeLang} onValueChange={(v) => setCodeLang(v as any)}>
                <TabsList>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="java">Java</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                </TabsList>

                <TabsContent value="nodejs" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUrl.nodejs}
                  </pre>
                </TabsContent>

                <TabsContent value="java" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUrl.java}
                  </pre>
                </TabsContent>

                <TabsContent value="python" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUrl.python}
                  </pre>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* 이미지 업로드 방식 */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>이미지 업로드 방식</CardTitle>
                  <CardDescription>이미지 파일을 직접 업로드하여 ALT 텍스트 생성 · API Key는 <a href="/dashboard/api-key" className="text-blue-600 underline">API Key 관리</a>에서 발급</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopyCode(sampleCodesUpload[codeLang])}
                >
                  {copiedCode ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      복사됨
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      복사
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <Tabs value={codeLang} onValueChange={(v) => setCodeLang(v as any)}>
                <TabsList>
                  <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                  <TabsTrigger value="java">Java</TabsTrigger>
                  <TabsTrigger value="python">Python</TabsTrigger>
                </TabsList>

                <TabsContent value="nodejs" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUpload.nodejs}
                  </pre>
                </TabsContent>

                <TabsContent value="java" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUpload.java}
                  </pre>
                </TabsContent>

                <TabsContent value="python" className="mt-4">
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-sm overflow-x-auto">
                    {sampleCodesUpload.python}
                  </pre>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>

        {/* 샘플 테스트 탭 */}
        <TabsContent value="test" className="space-y-6 mt-6">
          {!user ? (
            <Alert>
              <AlertDescription>
                샘플 테스트는 로그인 후 사용 가능합니다. 로그인하시면 각 방식당 1회씩 무료로 테스트할 수 있습니다.
              </AlertDescription>
            </Alert>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>API 테스트</CardTitle>
                <CardDescription>
                  고객님의 API Key를 입력해 주세요.
                </CardDescription>
              </CardHeader>
            <CardContent className="space-y-6">
              {/* 운영 API Key 입력 */}
              <div className="space-y-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                <Label htmlFor="manualApiKey" className="text-green-800 font-medium">API Key</Label>
                <Input
                  id="manualApiKey"
                  type="password"
                  placeholder="alt_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  value={manualApiKey}
                  onChange={(e) => setManualApiKey(e.target.value)}
                  disabled={testLoading}
                  className="font-mono text-sm"
                />

              </div>

              {/* 테스트 입력 영역 */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
                {/* 테스트 방식 선택 */}
                <div className="flex gap-4">
                  <Button
                    variant={testMethod === 'url' ? 'secondary' : 'outline'}
                    onClick={() => setTestMethod('url')}
                    disabled={testLoading}
                    className="flex-1"
                  >
                    <LinkIcon className="w-4 h-4 mr-2" />
                    이미지 URL
                  </Button>
                  <Button
                    variant={testMethod === 'file' ? 'secondary' : 'outline'}
                    onClick={() => setTestMethod('file')}
                    disabled={testLoading}
                    className="flex-1"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    이미지 업로드
                  </Button>
                </div>

                {/* URL 입력 */}
                {testMethod === 'url' && (
                  <div className="space-y-2">
                    <Label htmlFor="imageUrl">이미지 URL</Label>
                    <Input
                      id="imageUrl"
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      disabled={testLoading}
                    />
                  </div>
                )}

                {/* 이미지 업로드 */}
                {testMethod === 'file' && (
                  <div className="space-y-2">
                    <Label htmlFor="imageFile">이미지 파일</Label>
                    <Input
                      id="imageFile"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/tiff,image/avif,image/svg+xml,.jfif,.jpe"
                      onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                      disabled={testLoading}
                    />
                    {selectedFile && (
                      <div className="text-sm space-y-1">
                        <p className="text-gray-600">선택된 파일: {selectedFile.name}</p>
                        <p className="text-xs text-gray-500">
                          크기: {(selectedFile.size / 1024).toFixed(2)} KB
                          {selectedFile.size > MAX_UPLOAD_SIZE_BYTES && (
                            <span className="text-red-500 ml-2">⚠️ 파일이 너무 큽니다 ({MAX_UPLOAD_SIZE_LABEL})</span>
                          )}
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      * 지원 형식: JPG, PNG, GIF, WebP, TIFF, AVIF, SVG, JFIF ({MAX_UPLOAD_SIZE_LABEL})
                    </p>
                  </div>
                )}

                {/* 컨텍스트 프로필 옵션 */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="useContextProfile"
                    checked={useContextProfile}
                    onChange={(e) => setUseContextProfile(e.target.checked)}
                    disabled={testLoading || !profileActive}
                    className="w-4 h-4 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <label
                    htmlFor="useContextProfile"
                    className={`text-sm select-none ${profileActive ? 'cursor-pointer text-gray-700' : 'cursor-not-allowed text-gray-400'}`}
                  >
                    개인별 컨텍스트 프로필 적용
                    {profileActive
                      ? <span className="text-xs text-gray-500 ml-1">(설정 → 컨텍스트 프로필에서 업종/사이트 유형 등 설정 가능)</span>
                      : <span className="text-xs text-amber-600 ml-1">(컨텍스트 프로필을 먼저 등록해주세요 → 프로필 메뉴)</span>
                    }
                  </label>
                </div>
              </div>

              {/* API 키 안내 */}

              {/* 실행 버튼 */}
              <div className="flex gap-2">
                <Button
                  onClick={handleRunTest}
                  disabled={testLoading || (rateLimitRetryAfter !== null && rateLimitRetryAfter > 0)}
                  className="flex-1"
                >
                  {testLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {testStatus === 'PROCESSING' ? 'ALT 생성 중...' : '처리 중...'}
                    </>
                  ) : rateLimitRetryAfter !== null && rateLimitRetryAfter > 0 ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2" />
                      {rateLimitRetryAfter}초 후 재시도
                    </>
                  ) : (
                    <>
                      <FlaskConical className="w-4 h-4 mr-2" />
                      테스트 실행
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowSourceCode(!showSourceCode)}
                  disabled={testLoading}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  소스 보기
                </Button>
              </div>



              {/* 처리 중 상태 */}
              {testLoading && testStatus === 'PROCESSING' && (
                <Alert className="bg-blue-50 border-blue-200">
                  <AlertDescription>
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span>ALT 텍스트를 생성하고 있습니다. 잠시만 기다려주세요...</span>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {rateLimitRetryAfter !== null && rateLimitRetryAfter > 0 && (
                <Alert>
                  <AlertDescription>
                    요청 한도를 초과했습니다. <strong>{rateLimitRetryAfter}초</strong> 후 다시 시도해 주세요.
                  </AlertDescription>
                </Alert>
              )}

              {/* 오류 메시지 */}
              {testError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="whitespace-pre-line">{testError}</p>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* 결과 */}
              {testResult && testResult.status === 'COMPLETED' && (
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription>
                    <div className="space-y-3">
                      <p className="font-semibold text-green-800">✅ 테스트 성공!</p>
                      <div className="bg-white rounded p-4 text-sm space-y-2">
                        <div>
                          <strong>Request ID:</strong> 
                          <code className="ml-2 bg-gray-100 px-2 py-1 rounded">{testResult.requestId}</code>
                        </div>
                        <div>
                          <strong>테스트 방식:</strong> 
                          <span className="ml-2">{testResult.testMethod === 'URL' ? '이미지 URL' : '이미지 업로드'}</span>
                        </div>
                        <div>
                          <strong>상태:</strong>
                          <span className="ml-2">{testResult.status}</span>
                        </div>
                        <div>
                          <strong>생성 방식:</strong>
                          <span className="ml-2">{testResult.generationType || '-'}</span>
                        </div>
                        <div>
                          <strong>컨텍스트 프로필:</strong>
                          <span className="ml-2">{testResult.useContextProfile ? '적용' : '미적용'}</span>
                        </div>

                        {/* 이미지 미리보기 */}
                        {testResult.testMethod === 'FILE' && filePreviewUrl && (
                          <div>
                            <strong>업로드된 이미지:</strong>
                            <div className="mt-2">
                              <img
                                src={filePreviewUrl}
                                alt="업로드된 이미지 미리보기"
                                className="max-h-48 max-w-full rounded border object-contain bg-gray-50"
                              />
                            </div>
                          </div>
                        )}
                        {testResult.testMethod === 'URL' && testResult.imageUrl && testResult.imageUrl.startsWith('http') && (
                          <div>
                            <strong>이미지 URL:</strong>
                            <a href={testResult.imageUrl} target="_blank" rel="noopener noreferrer"
                               className="ml-2 text-blue-600 underline text-xs break-all">
                              {testResult.imageUrl}
                            </a>
                            <div className="mt-2">
                              <img
                                src={testResult.imageUrl}
                                alt="이미지 미리보기"
                                className="max-h-48 max-w-full rounded border object-contain bg-gray-50"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            </div>
                          </div>
                        )}

                        <div className="pt-2 border-t">
                          <strong>생성된 ALT 텍스트:</strong>
                          {testResult.altText ? (
                            <p className="mt-1 p-2 bg-gray-50 rounded border text-sm">{testResult.altText}</p>
                          ) : (
                            <p className="mt-1 p-2 bg-yellow-50 rounded border text-sm text-yellow-700">ALT 텍스트를 생성하지 못했습니다.</p>
                          )}
                        </div>
                        {testResult.createdAt && (
                          <div className="text-xs text-gray-500">
                            생성 시각: {new Date(testResult.createdAt).toLocaleString('ko-KR')}
                          </div>
                        )}
                      </div>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* 소스 코드 */}
              {showSourceCode && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h4 className="font-semibold mb-2 text-sm">
                    현재 테스트 소스 코드
                    <span className="ml-2 text-xs font-normal text-gray-500">
                      ({testMethod === 'url' ? '이미지 URL 방식' : '이미지 업로드 방식'})
                    </span>
                  </h4>
                  <pre className="bg-gray-800 text-gray-100 p-4 rounded text-xs overflow-x-auto">
{testMethod === 'url' ? 
`// 이미지 URL 방식 (JSON body)
// HTML에 결과 영역을 먼저 준비하세요:
// <div id="alt-result"></div>

const response = await fetch('https://alt.aigeneration-tech.com/api/open/alt/generate', {
  method: 'POST',
  headers: {
    'X-API-KEY': '${manualApiKey || 'YOUR_API_KEY'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    imageUrl: '${imageUrl || 'https://example.com/image.jpg'}',
    useContextProfile: ${useContextProfile}
  })
});
const result = await response.json();
const resultBox = document.getElementById('alt-result');

if (resultBox) {
  resultBox.innerHTML =
    '<div style="border:1px solid #bbf7d0;background:#f0fdf4;border-radius:8px;padding:16px;">' +
      '<p style="font-weight:700;color:#166534;">✅ 테스트 성공!</p>' +
      '<div style="background:#fff;border-radius:6px;padding:16px;font-size:14px;">' +
        '<div><strong>Request ID:</strong> <code>' + result.requestId + '</code></div>' +
        '<div><strong>테스트 방식:</strong> 이미지 URL</div>' +
        '<div><strong>상태:</strong> ' + result.status + '</div>' +
        '<div><strong>생성 방식:</strong> ' + (result.generationType || '-') + '</div>' +
        '<div><strong>컨텍스트 프로필:</strong> ' + (result.useContextProfile ? '적용' : '미적용') + '</div>' +
        '<div style="margin-top:12px;"><strong>이미지 URL:</strong> ' +
          '<a href="${imageUrl || 'https://example.com/image.jpg'}" target="_blank" rel="noopener noreferrer">${imageUrl || 'https://example.com/image.jpg'}</a>' +
          '<div style="margin-top:8px;"><img src="${imageUrl || 'https://example.com/image.jpg'}" alt="이미지 미리보기" style="max-height:192px;max-width:100%;border:1px solid #e5e7eb;border-radius:6px;object-fit:contain;" /></div>' +
        '</div>' +
        '<div style="margin-top:12px;border-top:1px solid #e5e7eb;padding-top:12px;">' +
          '<strong>생성된 ALT 텍스트:</strong>' +
          '<p style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px;">' + (result.altText || 'ALT 텍스트를 생성하지 못했습니다.') + '</p>' +
        '</div>' +
      '</div>' +
    '</div>'; 
}` :
`// 이미지 업로드 방식 (S3 Presigned URL)
// HTML에 파일 입력과 결과 영역을 먼저 준비하세요:
// <input id="image-file" type="file" accept="image/*" />
// <div id="alt-result"></div>

const selectedFile = document.getElementById('image-file').files[0];

// 1. Presigned URL 요청
const presignedRes = await fetch('https://alt.aigeneration-tech.com/api/open/alt/presigned-url', {
  method: 'POST',
  headers: {
    'X-API-KEY': '${manualApiKey || 'YOUR_API_KEY'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    filename: selectedFile.name,
    contentType: selectedFile.type
  })
});
const { uploadUrl, finalUrl } = await presignedRes.json();

// 2. S3에 직접 업로드
await fetch(uploadUrl, {
  method: 'PUT',
  body: selectedFile
});

// 3. ALT 생성 (s3Url 파라미터)
const response = await fetch('https://alt.aigeneration-tech.com/api/open/alt/generate', {
  method: 'POST',
  headers: {
    'X-API-KEY': '${manualApiKey || 'YOUR_API_KEY'}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    s3Url: finalUrl,
    useContextProfile: ${useContextProfile}
  })
});
const result = await response.json();
const previewUrl = URL.createObjectURL(selectedFile);
const resultBox = document.getElementById('alt-result');

if (resultBox) {
  resultBox.innerHTML =
    '<div style="border:1px solid #bbf7d0;background:#f0fdf4;border-radius:8px;padding:16px;">' +
      '<p style="font-weight:700;color:#166534;">✅ 테스트 성공!</p>' +
      '<div style="background:#fff;border-radius:6px;padding:16px;font-size:14px;">' +
        '<div><strong>Request ID:</strong> <code>' + result.requestId + '</code></div>' +
        '<div><strong>테스트 방식:</strong> 이미지 업로드</div>' +
        '<div><strong>상태:</strong> ' + result.status + '</div>' +
        '<div><strong>생성 방식:</strong> ' + (result.generationType || '-') + '</div>' +
        '<div><strong>컨텍스트 프로필:</strong> ' + (result.useContextProfile ? '적용' : '미적용') + '</div>' +
        '<div style="margin-top:12px;"><strong>업로드된 이미지:</strong>' +
          '<div style="margin-top:8px;"><img src="' + previewUrl + '" alt="업로드된 이미지 미리보기" style="max-height:192px;max-width:100%;border:1px solid #e5e7eb;border-radius:6px;object-fit:contain;" /></div>' +
        '</div>' +
        '<div style="margin-top:12px;border-top:1px solid #e5e7eb;padding-top:12px;">' +
          '<strong>생성된 ALT 텍스트:</strong>' +
          '<p style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px;">' + (result.altText || 'ALT 텍스트를 생성하지 못했습니다.') + '</p>' +
        '</div>' +
      '</div>' +
    '</div>';
}`}
                  </pre>
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
